import { CUSTOM_TRACKER_OPTIONS } from 'utils/facades/integrations-facade';
import { getDeviceType } from 'utils/environment';
import { LINK_ELEMENT_TRACKER_OPTIONS } from '../constants';

function _getDeviceType(): string {
	const type = getDeviceType();
	switch (type) {
		case 'desktop':
			return 'web';
		case 'tablet':
			return 'tabletApp';
		case 'mobile':
			return 'mobile';
		default:
			return type;
	}
}

export type ResolveCustomTrackerVariablesContext = {
	storyName: string;
	cardName: string;
	windowWidth?: number;
	linkUrl?: string;
};

/**
 * Resolves CUSTOM_TRACKER_OPTIONS placeholders in a string to their actual values.
 *
 * @param text - The text containing placeholders to resolve
 * @param context - Context object with required storyName, cardName, and optional windowWidth
 * @returns The text with all placeholders resolved to their actual values
 *
 * @example
 * resolveCustomTrackerVariables(
 *   'https://tracker.com?platform=$PLATFORM&story=$STORY_NAME',
 *   { storyName: 'My Story', cardName: 'Card 1', windowWidth: 1920, linkUrl: 'https://example.com' }
 * )
 * @returns: 'https://tracker.com?platform=web&story=My Story'
 */
export function resolveCustomTrackerVariables(text: string, context: ResolveCustomTrackerVariablesContext): string {
	const { storyName, cardName, windowWidth, linkUrl } = context;
	const variables = Object.values({ ...CUSTOM_TRACKER_OPTIONS, ...LINK_ELEMENT_TRACKER_OPTIONS }).filter(
		({ value }) => text.includes(value)
	);
	let resolvedText = text;

	variables.forEach(({ value }) => {
		let replacer: string;

		switch (value) {
			case '$STORY_NAME':
				replacer = storyName;
				break;
			case '$STORY_URL':
				replacer = typeof window !== 'undefined' ? window.location.href.replace(/\/c\/.*/, '') : '';
				break;
			case '$CARD_NAME':
				replacer = cardName;
				break;
			case '$PLATFORM':
				replacer = _getDeviceType();
				break;
			case '$REFERRER':
				replacer = typeof document !== 'undefined' ? document.referrer : '';
				break;
			case '$RESOLUTION':
				replacer = String(windowWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0));
				break;
			case '$LINK_URL':
				replacer = linkUrl ?? '';
				break;
			default:
				replacer = '';
				break;
		}

		resolvedText =
			typeof resolvedText === 'string' ? resolvedText.replaceAll(value, () => replacer) : String(resolvedText);
	});

	return resolvedText;
}

function safeDecodeURIComponent(value: string): string {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

/**
 * Resolves placeholders in a custom tracker url. Each query value is resolved separately and encoded,
 * so resolved values (e.g. story name "Q&A") can't break the query.
 *
 * @example
 * resolveCustomTrackerUrl('https://tracker.com?story=%24STORY_NAME', { storyName: 'Q&A', cardName: '' })
 * @returns: 'https://tracker.com?story=Q%26A'
 */
export function resolveCustomTrackerUrl(url: string, context: ResolveCustomTrackerVariablesContext): string {
	const searchIndex = url.indexOf('?');
	if (searchIndex < 0) {
		return resolveCustomTrackerVariables(url, context);
	}

	const query = url
		.slice(searchIndex + 1)
		.split('&')
		.map(pair => {
			const valueIndex = pair.indexOf('=');
			if (valueIndex < 0) return pair;

			// "+" means space in query string
			const rawValue = pair.slice(valueIndex + 1).replace(/\+/g, ' ');
			const value = resolveCustomTrackerVariables(safeDecodeURIComponent(rawValue), context);
			return `${pair.slice(0, valueIndex)}=${encodeURIComponent(value)}`;
		})
		.join('&');

	return `${resolveCustomTrackerVariables(url.slice(0, searchIndex), context)}?${query}`;
}
